import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { Doughnut, getElementAtEvent } from 'react-chartjs-2';
import {
  ArcElement,
  Chart as ChartJS,
  Legend,
  Tooltip
} from 'chart.js';
import buildApiUrl from '../config/apiUrlHelper';
import { icons } from './adminSidebarIcons';

ChartJS.register(ArcElement, Tooltip, Legend);

const centerTextPlugin = {
  id: 'centerTextPlugin',
  afterDraw: (chart, _, options) => {
    const text = options?.text;
    if (!text) return;

    const { ctx } = chart;
    const point = chart.getDatasetMeta(0)?.data?.[0];
    if (!point) return;
    const baseRadius = point.innerRadius || point.outerRadius || 70;
    const fontSize = Math.max(24, Math.min(42, Math.round(baseRadius * 0.42)));

    ctx.save();
    ctx.font = `800 ${fontSize}px ui-sans-serif, system-ui, -apple-system, Segoe UI, sans-serif`;
    ctx.lineWidth = Math.max(2, Math.round(fontSize * 0.12));
    ctx.strokeStyle = options?.strokeColor || 'rgba(15, 23, 42, 0.2)';
    ctx.fillStyle = options?.color || '#0f172a';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.strokeText(text, point.x, point.y);
    ctx.fillText(text, point.x, point.y);
    ctx.restore();
  }
};

ChartJS.register(centerTextPlugin);

const PALETTE = ['#0f766e', '#ea580c', '#1d4ed8', '#15803d', '#be185d', '#6d28d9', '#b45309', '#334155'];

const formatPercent = (value) => `${Number(value || 0).toFixed(2)}%`;

const PublicStatsSection = () => {
  const categoryChartRef = useRef(null);
  const tagChartRef = useRef(null);
  const mobileCombinedChartRef = useRef(null);
  const [categorias, setCategorias] = useState([]);
  const [etiquetas, setEtiquetas] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedTag, setSelectedTag] = useState('');
  const [totalPreguntas, setTotalPreguntas] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [lastUpdated, setLastUpdated] = useState('');
  const [isDarkMode, setIsDarkMode] = useState(() => {
    if (typeof document === 'undefined') return false;
    return document.documentElement.classList.contains('dark');
  });

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const root = document.documentElement;
    const syncTheme = () => setIsDarkMode(root.classList.contains('dark'));
    syncTheme();

    const observer = new MutationObserver(syncTheme);
    observer.observe(root, { attributes: true, attributeFilter: ['class'] });

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const htmlEl = document.documentElement;
    const bodyEl = document.body;
    htmlEl.classList.add('stats-no-scroll');
    bodyEl.classList.add('stats-no-scroll');

    return () => {
      htmlEl.classList.remove('stats-no-scroll');
      bodyEl.classList.remove('stats-no-scroll');
    };
  }, []);

  const centerTextStyle = useMemo(() => {
    if (isDarkMode) {
      return {
        color: '#e2e8f0',
        strokeColor: 'rgba(15, 23, 42, 0.35)'
      };
    }

    return {
      color: '#1e293b',
      strokeColor: 'rgba(248, 250, 252, 0.85)'
    };
  }, [isDarkMode]);

  // Separador de segmentos del donut según el tema (claro: blanco; oscuro: casi negro)
  const segmentBorderColor = isDarkMode ? '#181f2a' : '#ffffff';

  const fetchStats = useCallback(async (categoryValue = '') => {
    try {
      const query = categoryValue ? `?categoria=${encodeURIComponent(categoryValue)}` : '';
      const response = await axios.get(buildApiUrl(`obtener_estadisticas_preguntas.php${query}`), {
        withCredentials: true
      });

      const data = response.data || {};
      if (!data.success) {
        throw new Error(data.message || 'No se pudieron cargar las estadisticas');
      }

      const nextCategorias = data.categorias || [];
      const nextSelected = data.categoria_seleccionada || '';

      setCategorias(nextCategorias);
      setEtiquetas(data.etiquetas || []);
      setTotalPreguntas(data.total_preguntas || 0);
      setError('');
      setLastUpdated(data.updated_at || '');

      const nextEtiquetas = data.etiquetas || [];
      setSelectedTag((prev) => {
        if (nextEtiquetas.length === 0) return '';
        const exists = nextEtiquetas.some((item) => item.etiqueta === prev);
        return exists ? prev : nextEtiquetas[0].etiqueta;
      });

      setSelectedCategory((prev) => {
        const preferred = categoryValue || prev;
        const exists = nextCategorias.some((item) => item.categoria === preferred);
        if (exists) {
          return preferred;
        }
        return nextSelected;
      });
    } catch (requestError) {
      console.error('Error al obtener estadisticas publicas:', requestError);
      setError('No se pudieron cargar las estadisticas en este momento.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats(selectedCategory);
  }, [fetchStats, selectedCategory]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      fetchStats(selectedCategory);
    }, 15000);

    return () => clearInterval(intervalId);
  }, [fetchStats, selectedCategory]);

  const categoryChartData = useMemo(() => ({
    labels: categorias.map((item) => item.categoria),
    datasets: [
      {
        data: categorias.map((item) => item.total),
        backgroundColor: categorias.map((_, index) => PALETTE[index % PALETTE.length]),
        borderColor: segmentBorderColor,
        borderWidth: 4,
        borderRadius: 20,
        spacing: 4,
        hoverOffset: 8
      }
    ]
  }), [categorias, segmentBorderColor]);

  const categoryChartOptions = useMemo(() => ({
    cutout: '80%',
    responsive: true,
    animation: { duration: 400 },
    plugins: {
      legend: { display: false },
      centerTextPlugin: {
        text: formatPercent(categorias.find((item) => item.categoria === selectedCategory)?.porcentaje),
        color: centerTextStyle.color,
        strokeColor: centerTextStyle.strokeColor
      },
      tooltip: {
        callbacks: {
          label: (ctx) => {
            const item = categorias[ctx.dataIndex];
            if (!item) return '';
            return `${item.categoria}: ${item.total} (${formatPercent(item.porcentaje)})`;
          }
        }
      }
    }
  }), [categorias, selectedCategory, centerTextStyle]);

  const tagChartData = useMemo(() => ({
    labels: etiquetas.map((item) => item.etiqueta),
    datasets: [
      {
        data: etiquetas.map((item) => item.porcentaje),
        backgroundColor: etiquetas.map((_, index) => PALETTE[(index + 2) % PALETTE.length]),
        borderColor: segmentBorderColor,
        borderWidth: 4,
        borderRadius: 20,
        spacing: 4,
        hoverOffset: 8
      }
    ]
  }), [etiquetas, segmentBorderColor]);

  const tagChartOptions = useMemo(() => ({
    cutout: '80%',
    responsive: true,
    animation: { duration: 400 },
    plugins: {
      legend: { display: false },
      centerTextPlugin: {
        text: formatPercent(etiquetas.find((item) => item.etiqueta === selectedTag)?.porcentaje),
        color: centerTextStyle.color,
        strokeColor: centerTextStyle.strokeColor
      },
      tooltip: {
        callbacks: {
          label: (ctx) => {
            const item = etiquetas[ctx.dataIndex];
            if (!item) return '';
            return `${item.etiqueta}: ${formatPercent(item.porcentaje)} (${item.total})`;
          }
        }
      }
    }
  }), [etiquetas, selectedTag, centerTextStyle]);

  const mobileCombinedChartData = useMemo(() => ({
    labels: categorias.map((item) => item.categoria),
    datasets: [
      {
        label: 'Categorias',
        data: categorias.map((item) => item.total),
        backgroundColor: categorias.map((_, index) => PALETTE[index % PALETTE.length]),
        borderColor: segmentBorderColor,
        borderWidth: 3,
        borderRadius: 16,
        spacing: 3,
        hoverOffset: 6,
        radius: '100%',
        cutout: '68%'
      },
      {
        label: 'Etiquetas',
        data: etiquetas.map((item) => item.porcentaje),
        backgroundColor: etiquetas.map((_, index) => PALETTE[(index + 2) % PALETTE.length]),
        borderColor: segmentBorderColor,
        borderWidth: 2,
        borderRadius: 14,
        spacing: 1,
        hoverOffset: 5,
        radius: '67%',
        cutout: '56%'
      }
    ]
  }), [categorias, etiquetas, segmentBorderColor]);

  const mobileCombinedChartOptions = useMemo(() => ({
    responsive: true,
    animation: { duration: 400 },
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          title: (items) => {
            const firstItem = items?.[0];
            if (!firstItem) return '';

            if (firstItem.datasetIndex === 0) {
              return categorias[firstItem.dataIndex]?.categoria || '';
            }

            if (firstItem.datasetIndex === 1) {
              return etiquetas[firstItem.dataIndex]?.etiqueta || '';
            }

            return '';
          },
          label: (ctx) => {
            if (ctx.datasetIndex === 0) {
              const item = categorias[ctx.dataIndex];
              if (!item) return '';
              return `${item.categoria}: ${item.total} (${formatPercent(item.porcentaje)})`;
            }

            const item = etiquetas[ctx.dataIndex];
            if (!item) return '';
            return `${item.etiqueta}: ${formatPercent(item.porcentaje)} (${item.total})`;
          }
        }
      }
    }
  }), [categorias, etiquetas]);

  const selectedCategoryItem = useMemo(
    () => categorias.find((item) => item.categoria === selectedCategory) || null,
    [categorias, selectedCategory]
  );

  const selectedTagItem = useMemo(
    () => etiquetas.find((item) => item.etiqueta === selectedTag) || null,
    [etiquetas, selectedTag]
  );

  const handleCategoryChartClick = (event) => {
    if (!categoryChartRef.current) return;
    const elements = getElementAtEvent(categoryChartRef.current, event);
    if (!elements.length) return;
    const index = elements[0].index;
    const category = categorias[index]?.categoria;
    if (category && category !== selectedCategory) {
      setSelectedCategory(category);
    }
  };

  const handleTagChartClick = (event) => {
    if (!tagChartRef.current) return;
    const elements = getElementAtEvent(tagChartRef.current, event);
    if (!elements.length) return;
    const index = elements[0].index;
    const tag = etiquetas[index]?.etiqueta;
    if (tag && tag !== selectedTag) {
      setSelectedTag(tag);
    }
  };

  const handleMobileCombinedChartClick = (event) => {
    if (!mobileCombinedChartRef.current) return;

    const elements = getElementAtEvent(mobileCombinedChartRef.current, event);
    if (!elements.length) return;

    const { datasetIndex, index } = elements[0];

    if (datasetIndex === 0) {
      const category = categorias[index]?.categoria;
      if (category && category !== selectedCategory) {
        setSelectedCategory(category);
      }
      return;
    }

    if (datasetIndex === 1) {
      const tag = etiquetas[index]?.etiqueta;
      if (tag && tag !== selectedTag) {
        setSelectedTag(tag);
      }
    }
  };

  if (loading) {
    return <p className="text-sm text-slate-500 dark:text-slate-300">Cargando estadisticas...</p>;
  }

  if (error) {
    return <p className="text-sm text-red-600 dark:text-red-300">{error}</p>;
  }

  return (
    <section className="w-full overflow-hidden">
      <header className="mb-4 sm:mb-6">
        <h3 className="text-base sm:text-lg font-semibold text-slate-800 dark:text-white flex items-center gap-2">
          <span className="inline-flex text-blue-700 dark:text-blue-200" aria-hidden="true">
            {icons['estadisticas-publicas']}
          </span>
          <span>Estadisticas de preguntas</span>
        </h3>
        <p className="text-sm text-slate-500 dark:text-slate-300">
          Total preguntas aprobadas: {totalPreguntas}
          {/* {lastUpdated ? ` · Actualizado: ${new Date(lastUpdated).toLocaleTimeString()}` : ''} */}
        </p>
      </header>

      {categorias.length === 0 ? (
        <p className="text-sm text-slate-500 dark:text-slate-300">Todavia no hay preguntas aprobadas para mostrar.</p>
      ) : (
        <>
          <div className="sm:hidden w-full max-w-full overflow-x-hidden p-3 sm:p-4">
            <h4 className="text-sm font-semibold text-slate-700 dark:text-slate-200 mb-3">Categorias y etiquetas</h4>

            <div className="w-full max-w-[300px] mx-auto">
              <Doughnut
                ref={mobileCombinedChartRef}
                data={mobileCombinedChartData}
                options={mobileCombinedChartOptions}
                onClick={handleMobileCombinedChartClick}
              />
            </div>

            <div className="mt-4 p-1 space-y-1.5 text-sm">
              <p className="text-slate-700 dark:text-slate-200">
                <span className="font-semibold">Categoria:</span>{' '}
                <span>{selectedCategory || '-'}</span>{' '}
                <span className="font-semibold">{formatPercent(selectedCategoryItem?.porcentaje)}</span>
              </p>
              <p className="text-slate-700 dark:text-slate-200">
                <span className="font-semibold">Etiqueta:</span>{' '}
                <span>{selectedTag || '-'}</span>{' '}
                <span className="font-semibold">{formatPercent(selectedTagItem?.porcentaje)}</span>
              </p>
            </div>
          </div>

          <div className="hidden sm:grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 xl:gap-8 items-start">
          <div className="p-3 sm:p-4">
            <h4 className="text-sm font-semibold text-slate-700 dark:text-slate-200 mb-4">Por categoria</h4>
            <div className="max-w-[260px] sm:max-w-[340px] mx-auto">
              <Doughnut ref={categoryChartRef} data={categoryChartData} options={categoryChartOptions} onClick={handleCategoryChartClick} />
            </div>
            <p className="mt-4 text-sm text-slate-600 dark:text-slate-300 text-center">
              Categoria seleccionada: <span className="font-semibold">{selectedCategory}</span>
            </p>
          </div>

          <div className="p-3 sm:p-4">
            <h4 className="text-sm font-semibold text-slate-700 dark:text-slate-200 mb-4">
              Por etiqueta dentro de {selectedCategory || 'la categoria seleccionada'}
            </h4>

            {etiquetas.length === 0 ? (
              <p className="text-sm text-slate-500 dark:text-slate-300">No hay etiquetas disponibles para esta categoria.</p>
            ) : (
              <>
                <div className="max-w-[260px] sm:max-w-[340px] mx-auto">
                  <Doughnut ref={tagChartRef} data={tagChartData} options={tagChartOptions} onClick={handleTagChartClick} />
                </div>
                <p className="mt-4 text-sm text-slate-600 dark:text-slate-300 text-center">
                  Etiqueta seleccionada: <span className="font-semibold">{selectedTag}</span>
                </p>
              </>
            )}
          </div>
          </div>
        </>
      )}
    </section>
  );
};

export default PublicStatsSection;
